import { isEmail } from '@seek/validators-js';
import { useTranslations } from '@vocab/react';
import { Actions, Button, TextField, Stack } from 'braid-design-system';
import React, { type FormEventHandler } from 'react';

import ServerError from 'src/components/Account/ServerError/ServerError';
import { useAnalytics } from 'src/contexts/AnalyticsContext';
import type { ServerError as ServerErrorType } from 'src/types';

import translations from './.vocab';
import type { EmailUseField } from './FormContainer';

export const FORM_NAME = 'changeEmail';

interface Props {
  onSubmit: FormEventHandler;
  submitting: boolean;
  toggle: () => void;
  error?: ServerErrorType | null;
  useField: EmailUseField;
}

const Form = ({ onSubmit, submitting, toggle, error, useField }: Props) => {
  const { t } = useTranslations(translations);
  const { trackAccountLink } = useAnalytics();

  const newEmail = useField({
    id: 'newEmail',
    validators: [isEmail],
  });

  const onCancel = () => {
    trackAccountLink({
      eventName: 'cancel_pressed',
      currentPageSection: 'change email',
      passwordlessAuthentication: 'full',
      identityProvider: 'auth0',
      authenticationFlow: 'changeEmail',
    });

    toggle();
  };

  return (
    <form onSubmit={onSubmit} role="form">
      <Stack space="medium">
        <TextField
          id="newEmail"
          name="newEmail"
          label={t('New email address')}
          type="email"
          reserveMessageSpace
          {...newEmail}
        />

        {error && !submitting ? (
          <ServerError error={error} currentPageSection="change email" />
        ) : null}
        <Actions>
          <Button type="submit" tone="formAccent" loading={submitting}>
            {t('Continue')}
          </Button>
          <Button variant="transparent" tone="formAccent" onClick={onCancel}>
            {t('Cancel')}
          </Button>
        </Actions>
      </Stack>
    </form>
  );
};

export default Form;
